import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { AxiosError } from "axios";
import React, { useEffect } from "react";
import { useForm } from "react-hook-form";

import {
  VERSION_DESCRIPTION_MAX_LENGTH,
  VERSION_NAME_MAX_LENGTH,
  VERSION_NAME_MIN_LENGTH,
} from "src/agents/constants";
import { useUpdateAgentVersion } from "src/agents/queries/management";
import { AgentVersion } from "src/agents/types";
import { Button } from "src/base-components/Button";
import { ErrorHint } from "src/base-components/ErrorHint";
import { Icon } from "src/base-components/Icon";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { Textarea } from "src/base-components/Textarea";
import { Modal } from "src/design-system/Modal";
import { toastActions } from "src/design-system/Toast/utils";
import { Tooltip } from "src/design-system/Tooltip";
import { logger } from "src/utils/logger";

type EditAgentVersionModalProps = {
  open: boolean;
  onClose: () => void;
  version: AgentVersion | null;
};

type EditAgentVersionForm = {
  name: string;
  description: string;
};

export const EditAgentVersionModal: React.FC<EditAgentVersionModalProps> = ({
  open,
  onClose,
  version,
}) => {
  const { mutateAsync: updateVersion } = useUpdateAgentVersion();

  const form = useForm<EditAgentVersionForm>({
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
    },
  });

  useEffect(() => {
    if (!open) return; // Prevent form reset when modal is closed so it doesn't flicker
    if (version) {
      form.reset({
        name: version.name,
        description: version.description,
      });
    } else {
      form.reset({
        name: "",
        description: "",
      });
    }
  }, [version, form, open]);

  const onSubmit = async (data: EditAgentVersionForm) => {
    if (!version) return;

    try {
      await updateVersion({
        agentId: version.agent_id,
        versionId: version.id,
        etag: version.etag,
        name: data.name,
        description: data.description,
        configuration: version.configuration,
      });
      toastActions.success({
        title: "Version updated successfully",
      });
      onClose();
    } catch (error) {
      logger.error("Failed to update version", error);
      const errorMessage =
        error instanceof AxiosError && error.status === 409
          ? "Version name already exists"
          : "An unknown error occurred";
      toastActions.failure({
        title: "Failed to update version",
        description: errorMessage,
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Modal.Header>Edit version</Modal.Header>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Modal.Content>
          <div className="flex flex-col gap-5">
            <div>
              <Label required>Version name</Label>
              <Input
                errored={!!form.formState.errors.name}
                placeholder="Type version name"
                fullWidth
                {...form.register("name", {
                  required: "Version name is required",
                  minLength: {
                    value: VERSION_NAME_MIN_LENGTH,
                    message: `Version name must be at least ${VERSION_NAME_MIN_LENGTH} character`,
                  },
                  maxLength: {
                    value: VERSION_NAME_MAX_LENGTH,
                    message: `Version name must be less than ${VERSION_NAME_MAX_LENGTH} characters`,
                  },
                })}
              />
              {form.formState.errors.name && (
                <ErrorHint>{form.formState.errors.name.message}</ErrorHint>
              )}
            </div>

            <div>
              <div className="mb-2 flex items-center gap-1">
                <Label mb="mb-0">Description</Label>
                <Tooltip
                  body="Adding a description helps others understand what this version does and how it differs from other versions."
                  placement="right-start"
                >
                  <Icon color="text-gray-500" icon={faInfoCircle} size="xs" />
                </Tooltip>
              </div>
              <Textarea
                errored={!!form.formState.errors.description}
                placeholder="Type version description"
                fullWidth
                {...form.register("description", {
                  maxLength: {
                    value: VERSION_DESCRIPTION_MAX_LENGTH,
                    message: `Description must be less than ${VERSION_DESCRIPTION_MAX_LENGTH} characters`,
                  },
                })}
              />
              {form.formState.errors.description && (
                <ErrorHint>
                  {form.formState.errors.description.message}
                </ErrorHint>
              )}
            </div>
          </div>
        </Modal.Content>
        <Modal.Footer
          primaryButton={
            <Button
              disabled={form.formState.isSubmitting || !form.formState.isValid}
              htmlType="submit"
              loading={form.formState.isSubmitting}
              variant="primary"
            >
              Save
            </Button>
          }
        />
      </form>
    </Modal>
  );
};
